import React, { useEffect, useState } from 'react';
import { Form, Input } from 'antd';
import headAvatar from '@/assets/images/HeadAvatar.svg';
import CustomTitle from '@/components/CustomTitle.jsx';
import CustomButton from '@/components/CustomButton.jsx';
import Flag from '../DirectContactList/Flag';

const { TextArea } = Input;

export interface DirectoryNoteScreenProps {
  contact?: {
    id: string | number;
    name?: string;
    email?: string;
    phone?: string;
    country?: string;
    hours?: string;
  } | null;
  defaultValue?: string;
  onCancel?: () => void;
  onSave?: (val: string) => void;
}

/**
 * Form screen for adding or editing notes on a directory contact.
 * Displays contact information (name, email, phone, hours) and provides a textarea for note entry.
 *
 * @param contact - Contact information to display
 * @param defaultValue - Initial value for the note textarea
 * @param onCancel - Callback function triggered when Cancel button is clicked
 * @param onSave - Callback function triggered when Save button is clicked, receives the note value
 */
const DirectoryNoteScreen: React.FC<DirectoryNoteScreenProps> = ({
  contact,
  defaultValue = '',
  onCancel,
  onSave,
}) => {
  const [form] = Form.useForm();
  const [value, setValue] = useState(defaultValue);

  useEffect(() => {
    form.setFieldsValue({ note: defaultValue });
    setValue(defaultValue);
  }, [defaultValue, form]);

  return (
    <div style={{ display: 'flex', flexDirection: 'column', height: '100%' }}>
      <div style={{ paddingBottom: 14 }}>
        <div style={{ display: 'flex', gap: 12, alignItems: 'center' }}>
          <img src={headAvatar} alt='' style={{ width: 28, height: 28 }} />

          <div style={{ minWidth: 0 }}>
            <CustomTitle
              text={contact?.name ?? '-'}
              fontSize='16px'
              lineHeight='20px'
              fontWeight={600}
              color='var(--text-primary)'
              marginBottom={0}
              style={{ marginBottom: 2 }}
            />

            {contact?.email && (
              <CustomTitle
                text={contact.email}
                fontSize='12px'
                marginBottom={0}
                lineHeight='16px'
                fontWeight={400}
                color='var(--text-secondary)'
                style={{ marginTop: 2 }}
              />
            )}

            {contact?.phone && (
              <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                {contact?.country && <Flag country={contact.country} />}
                <CustomTitle
                  text={contact.phone}
                  fontSize='12px'
                  marginBottom={0}
                  lineHeight='16px'
                  fontWeight={400}
                  color='var(--text-secondary)'
                />
              </div>
            )}

            {contact?.hours && (
              <CustomTitle
                text={contact.hours}
                fontSize='12px'
                marginBottom={0}
                lineHeight='16px'
                fontWeight={400}
                color='var(--text-secondary)'
                style={{ marginTop: 2 }}
              />
            )}
          </div>
        </div>
      </div>

      <div style={{ flex: 1, overflowY: 'auto', paddingBottom: 120 }}>
        <Form form={form} layout='vertical' onFinish={() => onSave?.(value)}>
          <Form.Item label='Note' name='note'>
            <TextArea
              rows={3}
              placeholder='Type your note here...'
              value={value}
              onChange={(e) => setValue(e.target.value)}
              style={{
                paddingTop: 10,
                resize: 'none',
                background: 'var(--light-gray)',
                border: 'none',
                boxShadow: 'none',
              }}
            />
          </Form.Item>
        </Form>
      </div>

      <div
        style={{
          background: 'var(--surface-container)',
          position: 'absolute',
          bottom: 0,
          left: 0,
          width: '100%',
          padding: 16,
        }}
      >
        <CustomButton
          text='Save'
          bgColor='#000'
          color='#fff'
          fontSize='16px'
          borderRadius='8px'
          fontWeight='600'
          height='48px'
          width='100%'
          onClick={() => onSave?.(value)}
        />
        <CustomButton
          boxShadow='none'
          text='Cancel'
          bgColor='transparent'
          color='var(--text-primary)'
          fontSize='16px'
          fontWeight='400'
          height='auto'
          width='100%'
          borderRadius='8px'
          border='none'
          marginTop='20px'
          marginBottom='10px'
          onClick={onCancel}
        />
      </div>
    </div>
  );
};

export default DirectoryNoteScreen;
